import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { initAffiliateTracking } from '../lib/affiliateTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Must run before anything else – reads ?t= from URL and saves to localStorage
    initAffiliateTracking();
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      // Detect browser language, e.g. "tr-TR" → "tr"
      const lang = (navigator.language || navigator.userLanguage || 'en')
        .split('-')[0]
        .toLowerCase();

      // 1. Try user's detected language (skip the extra request if already 'en')
      if (lang !== 'en') {
        const langRes = await axios.get(`${BACKEND_URL}/api/quizzes`, {
          params: { language: lang },
        });
        if (langRes.data?.length > 0) {
          setQuizzes(langRes.data);
          return;
        }
      }

      // 2. Fall back to English
      const enRes = await axios.get(`${BACKEND_URL}/api/quizzes`, {
        params: { language: 'en' },
      });
      setQuizzes(enRes.data || []);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const truncateName = (name, maxLength = 30) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength - 3) + '...';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">QuizMind</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-2 md:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
              {t('home.title')}
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              {t('home.subtitle')}
            </p>
          </motion.div>

          {/* Quiz Grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-3xl p-6 animate-pulse" style={{ height: 'auto', minHeight: '320px' }} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 auto-rows-fr">
              {quizzes.map((quiz, index) => (
                <motion.div
                  key={quiz.quiz_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  onClick={() => navigate(`/quiz/${quiz.quiz_id}`)}
                  className="group relative overflow-hidden bg-white rounded-3xl p-3 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border border-slate-100 cursor-pointer flex flex-col"
                  data-testid={`quiz-card-${quiz.quiz_id}`}
                >
                  <div className="aspect-square relative mb-3 sm:mb-4 overflow-hidden rounded-2xl flex-shrink-0">
                    <img
                      src={quiz.quiz_image}
                      alt={quiz.quiz_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 mb-2 line-clamp-2 flex-grow">
                    {truncateName(quiz.quiz_name)}
                  </h3>
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <span className="text-xs sm:text-sm font-medium text-violet-600">
                      {t('home.cta')}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-sm border-t border-slate-100 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">				  
              <button
                onClick={() => navigate('/affiliate')}
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="privacy-policy-link"
              >
                Affiliate
              </button>
			  <span className="hidden sm:inline text-slate-300">•</span>
              <button
                onClick={() => navigate('/about')}
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="about-us-link"
              >
                {t('footer.aboutUs', 'About Us')}
              </button>
              <span className="hidden sm:inline text-slate-300">•</span>
              <button
                onClick={() => navigate('/terms')}
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="terms-of-service-link"
              >
                {t('footer.tos')}
              </button>
              <span className="hidden sm:inline text-slate-300">•</span>
              <button
                onClick={() => navigate('/privacy')}
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="privacy-policy-link"
              >
                {t('footer.privacyPolicy')}
              </button>
            </div>
            <p className="text-sm text-slate-600">{t('footer.company')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}